/**
 * /connect — logging into an app, as its own address.
 *
 * An app keeps the person's `user_id` in its own cookie, and every Paradigm call
 * needs `X-User-ID`, so an app that loses that cookie cannot ask Paradigm
 * anything — not even who the visitor is. Nothing answers that question, and the
 * one place a user_id has ever reached an app is the query string on the consent
 * callback. So "my cookie expired" and "I need consent" were the same state, and
 * both led to /authorize, whose only move is to ask.
 *
 * The result was a consent screen for grants that had been live for months.
 * Verified on a live account: clearing one app's cookie and nothing else — grant
 * untouched, Paradigm session untouched, signed in one tab away — produced the
 * full card, and approving it left the consent blob byte-identical. The screen
 * was never asking anything. It was fetching an ID.
 *
 * So the two operations get two addresses. /connect never shows a screen;
 * /authorize always does. An app that wants to log someone in says so, and an
 * app that wants consent says that instead, rather than both being spelled the
 * same way and one of them lying.
 *
 * There is no page here on purpose. It reads a token from localStorage —
 * which is why this cannot be a bare backend redirect, since a browser carries
 * no Paradigm cookie — makes one call, and leaves. Everything that decides is
 * on the server: whether a grant exists, whether it still satisfies the app,
 * whether the redirect_uri is one the app registered, and who the person is.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { appCallbackUrl } from '../../components/grant/appCallback';
import { GrantFrame } from '../../components/grant/GrantPage';

export default function ConnectRoute() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(null);
  // A ref, not state: this must not re-arm on re-render, and React's double
  // invocation of mount effects in development would otherwise POST twice.
  const went = useRef(false);

  useEffect(() => {
    if (went.current) return;
    const clientId = params.get('client_id');
    // `return_url` is ink's spelling of the same thing, accepted here for the
    // same reason /authorize accepts it.
    const redirectUri = params.get('redirect_uri') || params.get('return_url');

    // CONSENT IS THE FALLBACK FOR EVERYTHING, including the cases this route
    // cannot serve. A missing client_id, a person with no grant, an app that now
    // asks for more, an unpaid app: all of them are questions /authorize exists
    // to put, and none of them are errors to strand someone on.
    //
    // NAVIGATED, not reloaded. /authorize is a route in this same app and
    // GrantRoute is statically imported, so it is already parsed and in memory;
    // assigning window.location.href would throw that away and re-fetch the
    // whole bundle, re-boot React and re-run the auth bootstrap to render a
    // component the browser was already holding — on what is one of this
    // route's two normal outcomes, not a rare path. `replace` because /connect
    // is a redirector: Back should reach whatever preceded it, not bounce
    // through here again.
    const toConsent = () => navigate(
      { pathname: '/authorize', search: `?${new URLSearchParams(params)}` },
      { replace: true },
    );
    if (!clientId || !redirectUri) return toConsent();

    went.current = true;
    (async () => {
      try {
        const r = await api.startAppLogin({ client_id: clientId, redirect_uri: redirectUri });
        if (r?.status !== 'ok') return toConsent();
        window.location.href = appCallbackUrl({
          redirectUri,
          clientId,
          // FROM THE SERVER, not from local auth state. The callback's whole job
          // is to carry the identity, and a browser holding a token but not yet
          // the profile behind it would hand the app an empty `user_id=` —
          // which every app reads as no user at all.
          userId: r.user_id,
          username: r.username,
          sidCode: r.code,
        });
      } catch (e) {
        // Both branches are terminal — show the error, or leave for /authorize —
        // so `went` stays set. Clearing it would only mean "POST again if this
        // ever runs a second time", which is a duplicate mint, not a recovery.
        //
        // A registered-callback mismatch is the developer's to fix and the one
        // thing consent cannot rescue: sending them to /authorize would only
        // approve a grant and then hand control to the same bad address.
        if (e?.code === 'INVALID_REDIRECT_URI') {
          setFailed('This app sent a callback address it has not registered.');
          return;
        }
        toConsent();
      }
    })();
  }, [params, navigate]);

  if (failed) {
    return (
      <GrantFrame className="gp-route">
        <div className="gp-route--error" role="alert">{failed}</div>
      </GrantFrame>
    );
  }
  return <GrantFrame className="gp-route" data-testid="connecting" aria-busy="true" />;
}
